import { Icon } from "@msuite/ui/icons";
import { Popover } from "@msuite/ui/popover";
import { useQuery } from "@msuite/ui/query";
import { Spinner } from "@msuite/ui/spinner";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type { SubstitutionPlanFrontendFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { UserCard } from "@/components/cards";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useSubstitutionPlanFormContext } from "./context";

/** Props Interface */
interface SubstitutionPlanHintsProps {
	itemId: string;
}

export function SubstitutionPlanHints({ itemId }: SubstitutionPlanHintsProps) {
	const { getItemIndex, absenceId, startDate, endDate } = useSubstitutionPlanFormContext();
	const index = getItemIndex(itemId);
	const methods = useFormContext<SubstitutionPlanFrontendFormSchema>();
	const { user } = useAuth();

	const currentlyAssignedUserIds = useWatch({
		control: methods.control,
		name: `items.${index}.assignedUserIds`,
	});

	const { data, isFetching } = useQuery({
		queryKey: [absenceId, startDate, endDate].concat(guardArray(currentlyAssignedUserIds)),
		queryFn: async () => {
			if (!currentlyAssignedUserIds.length) {
				return {
					hasCollision: false,
					userIds: [],
					success: true,
				};
			}
			const response = await client.users[":userId"].substitutions[":absenceId"].collision.$get({
				param: {
					userId: guardString(user?.id),
					absenceId,
				},
				query: {
					assignedUserIds: guardArray(currentlyAssignedUserIds),
					startDate,
					endDate,
				},
			});
			const data = await responseHandler(response);
			return data;
		},
	});

	/** Render */
	return (
		<div className="h-stack justify-end absolute inset-0 px-3">
			{isFetching ? (
				<Spinner />
			) : (
				<React.Fragment>
					{data?.hasCollision ? (
						<Popover
							trigger="hover"
							placement="bottom-end"
							render={() => (
								<div className="v-stack">
									{data?.userIds.map((userId) => (
										<UserCard
											userId={userId}
											key={userId}
											variant="plain"
										/>
									))}
								</div>
							)}
						>
							<Icon
								name="TriangleAlert"
								className="fill-orange-500/10 stroke-orange-500"
							/>
						</Popover>
					) : (
						<React.Fragment />
					)}
				</React.Fragment>
			)}
		</div>
	);
}
